import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { resumeIndex, visibleCoachSteps } from '@/guidance/coach';
import { coachEntries, continueCoach, startCoach, type CoachEntry } from '@/guidance/coaching';
import { useGuidanceInternals, type GuidanceInternals } from '@/guidance/context';
import { availableTours, visibleSteps } from '@/guidance/eligibility';
import { excludedRouteFor } from '@/guidance/safeRoutes';
import { endsTheSession } from '@/guidance/session';
import type { TourDefinition } from '@/guidance/types';
import { ApiError } from '@/shared/api/client';
import { Button } from '@/shared/components/Button';
import { Icon } from '@/shared/components/Icon';
import { SearchInput } from '@/shared/components/SearchInput';
import { LoadingState } from '@/shared/components/States';
import { StatusBadge } from '@/shared/components/StatusBadge';
import { useLocale } from '@/shared/i18n/useLocale';
import type { Locale } from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';
import { foreignLang } from '@/shared/utils/textLang';

import { QuickFindContext, useQuickFindControl, type QuickFindControl } from './context';
import { LOCAL_LIMIT, labelRanges, matchesLabel, matchPlaces, type QuickFindPlace } from './goto';
import { Highlight } from './Highlight';
import { isExactCode, substringRanges, type TextRange } from './match';
import { quickFindPrefix } from './queryKeys';
import { useRecentSearches, type RecentSearches } from './recent';
import { destinationPath, seeAllPath } from './routes';
import {
  ariaKeyShortcuts,
  isApplePlatform,
  matchesShortcut,
  shortcutBlocked,
  shortcutHint,
} from './shortcut';
import { DEBOUNCE_MS, MAX_TERM, normaliseTerm } from './term';
import type { QuickFindGroup, QuickFindItem, SearchPortal } from './types';
import { useQuickFind, type QuickFindView } from './useQuickFind';

/**
 * Quick Find — one search entry per signed-in shell (CONTRACT §4), a command
 * and search surface (PUX §10).
 *
 * An owner-requested local extension, not a Matrix capability (CONTRACT §1):
 * it reads only the three guarded search routes, which reuse each list's own
 * scope and policy on the server. R-050 has no combobox or command component,
 * so the overlay is composed from what it does have — a native modal
 * `<dialog>` built like `Sheet` and `Modal`, the shared `SearchInput`, the
 * shared states and `StatusBadge` — and that gap is recorded.
 *
 * - Native `<dialog>` opened with `showModal()`: the platform makes the page
 *   inert, traps focus and owns the top layer, and the guide engine treats it
 *   as any other application dialog (it stands aside; a coach yields).
 * - A combobox over a grouped listbox: arrow keys, Home and End move, Enter
 *   opens, Escape closes, focus returns to whatever opened it.
 * - Results are text. Nothing is rendered as HTML: a match is marked with
 *   `<mark>` around text nodes (QF-01), and choosing a row only NAVIGATES —
 *   the record page re-authorises it like any visit.
 * - "Best match" (QF-02): a returned row whose code IS the code typed comes
 *   first and is the active row, so an exact number and Enter opens it. Only
 *   rows the server returned are ever compared.
 * - Counts (QF-05) are what is ON SCREEN: "5 shown · more available". No
 *   total is asked for or implied (CONTRACT §2).
 * - Recent searches (QF-07, D-PUX-07): typed terms only, in this tab only,
 *   per person, never on the operator console (`recent.ts`). Nothing else
 *   persists: results go when the overlay closes, when the person changes
 *   (the shell remounts this provider by identity) and on sign-out.
 * - Absent on sign-in, MFA, `/actions/:token` and activation (those screens
 *   are outside every shell) and on the sign-in security pages.
 * - Besides records, local groups matched in the browser on labels the shell
 *   already holds, in both languages (QF-09): "Go to" (the navigation this
 *   seat has, with its account pages), "Start" (the create pages this seat's
 *   own buttons open, QF-14) and "Help" (coaches with their progress, QF-12,
 *   and guides). A guide or coach starts only once the overlay has closed.
 */
export function QuickFindProvider({
  portal,
  identity,
  families,
  destinations,
  children,
}: {
  portal: SearchPortal;
  /** Who is searching — the session's opaque ids (`identityKey`). */
  identity: string;
  /** The families this person may search, in the portal's order. Empty: no entry. */
  families: readonly string[];
  /**
   * G-1 — the places the shell's navigation offers THIS seat, already
   * filtered by the same permission rules as the navigation itself, and the
   * "Start" commands its own pages offer it (`kind: 'command'`).
   */
  destinations?: readonly QuickFindPlace[];
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  // Open ON a page: moving to another page (a result, Back, a link) closes
  // it, and a page that shows a secret has no entry and no overlay.
  const [intent, setIntent] = useState({ pathname, open: false });
  // A page change consumes its old open intent before children render. Merely
  // hiding by pathname would let Back, or a later link, reopen a dormant modal.
  if (intent.pathname !== pathname) setIntent({ pathname, open: false });
  const apple = useMemo(() => isApplePlatform(), []);
  const recent = useRecentSearches(portal, identity);

  const secret = excludedRouteFor(pathname)?.currentPageGuide === false;
  const available = families.length > 0 && !secret;
  const open = available && intent.pathname === pathname && intent.open;
  const placeholder = useMemo(() => placeholderFor(families, locale, t), [families, locale, t]);

  const show = useCallback(() => {
    if (available)
      setIntent((current) =>
        current.pathname === pathname ? { ...current, open: true } : current,
      );
  }, [available, pathname]);
  const hide = useCallback(() => setIntent((current) => ({ ...current, open: false })), []);

  // Nothing is kept: the results go as soon as the overlay is closed, and
  // when this provider unmounts (sign-out, another identity).
  useEffect(() => {
    if (!open) queryClient.removeQueries({ queryKey: quickFindPrefix(portal) });
  }, [open, portal, queryClient]);

  useEffect(
    () => () => queryClient.removeQueries({ queryKey: quickFindPrefix(portal) }),
    [portal, queryClient],
  );

  // What the shortcut does, swapped after each commit as it always was: a key
  // pressed while a navigation commits still reaches the page it was pressed
  // on, whose open intent the new page has already consumed.
  const shortcut = useRef({ available, apple, show });
  useEffect(() => {
    shortcut.current = { available, apple, show };
  }, [available, apple, show]);

  // Listening from the commit that first shows the entry. Since OBS-007 the
  // shell first renders from an update made inside an effect, and an ordinary
  // effect of such a render runs a task later: a Ctrl K pressed in between,
  // with the entry already on the screen, met no listener and was lost (the
  // rc3.2 final matrix, 29 October 2026).
  useLayoutEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const current = shortcut.current;
      if (!current.available || !matchesShortcut(event, current.apple) || shortcutBlocked(event))
        return;

      event.preventDefault();
      current.show();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const control = useMemo<QuickFindControl>(
    () => ({
      portal,
      available,
      open,
      placeholder,
      shortcutHint: shortcutHint(apple),
      ariaKeyShortcuts: ariaKeyShortcuts(apple),
      show,
    }),
    [portal, available, open, placeholder, apple, show],
  );

  return (
    <QuickFindContext.Provider value={control}>
      {children}
      {available ? (
        <QuickFindDialog
          open={open}
          onClose={hide}
          portal={portal}
          identity={identity}
          placeholder={placeholder}
          families={families}
          destinations={destinations ?? NO_PLACES}
          recent={recent}
        />
      ) : null}
    </QuickFindContext.Provider>
  );
}

function scopeOf(
  families: readonly string[],
  locale: Locale,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return new Intl.ListFormat(locale, { type: 'conjunction' }).format(
    families.map((family) => t(`search.scope.${family}`)),
  );
}

function placeholderFor(
  families: readonly string[],
  locale: Locale,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (families.length === 0) return t('search.placeholderAny');

  return t('search.placeholder', { scope: scopeOf(families, locale, t) });
}

/** The trigger's shapes; each opens the same overlay. */
export type QuickFindTriggerShape = 'field' | 'icon' | 'labelled';

/**
 * IA-04 / QF-03 — one entry rule for the three shells:
 * - `field`    the tenant desk's field-shaped button (search glyph, scope,
 *              shortcut); a button, not an input — nothing is typed in the bar;
 * - `labelled` glyph + "Search" + the shortcut, for the supplier and operator
 *              desks;
 * - `icon`     a 44px glyph button named "Search", in the header line at every
 *              phone width.
 */
export function QuickFindTrigger({
  shape,
  className,
}: {
  shape: QuickFindTriggerShape;
  className?: string;
}) {
  const { t } = useTranslation();
  const control = useQuickFindControl();

  if (control === null || !control.available) return null;

  const common = {
    'aria-haspopup': 'dialog' as const,
    'aria-expanded': control.open,
    'aria-keyshortcuts': control.ariaKeyShortcuts,
    'data-quickfind-trigger': shape,
    onClick: () => control.show(),
  };
  const hint = (
    <kbd
      aria-hidden="true"
      className="numeric type-meta shrink-0 rounded border border-current/30 px-1.5"
    >
      {control.shortcutHint}
    </kbd>
  );

  if (shape === 'field') {
    return (
      <button
        type="button"
        {...common}
        aria-label={control.placeholder}
        className={cn(
          'flex min-h-11 min-w-0 items-center gap-2 rounded-lg bg-card px-3 text-start text-ink-muted ring-1 ring-line hover:bg-hover focus-visible:outline-2 focus-visible:outline-focus',
          className,
        )}
      >
        <Icon name="search" size={16} className="shrink-0" />
        <span className="type-ui min-w-0 flex-1 truncate">{control.placeholder}</span>
        {hint}
      </button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      {...common}
      aria-label={shape === 'icon' ? t('search.entry') : undefined}
      className={cn('min-w-11 justify-center gap-1.5 whitespace-nowrap', className)}
    >
      <Icon name="search" size={18} className="shrink-0" />
      {shape === 'labelled' ? (
        <>
          <span>{t('search.entry')}</span>
          {hint}
        </>
      ) : null}
    </Button>
  );
}

/** The groups' names, beside the record families (never sent to the server). */
const BEST = 'best';
const RECENT = 'recent';
const PLACES = 'navigation';
const COMMANDS = 'commands';
const GUIDES = 'help';

/**
 * QF-11 — the families whose own list searches more fields than Quick Find
 * does (requests: the business reason; suppliers: registration, VAT, email).
 * Their "See all" stays offered when Quick Find found nothing, and "nothing"
 * is said as "no number or name matches", never as "no matches".
 */
const WIDER_LISTS: ReadonlySet<string> = new Set(['requests', 'suppliers']);

/**
 * One row the keyboard can reach. Local rows (recent terms, places, Start
 * commands, coaches and guides) are matched here; records come from the
 * server; "See all" opens a list with its own search.
 */
type Choice =
  | { kind: 'recent'; id: string; family: typeof RECENT; term: string }
  | { kind: 'clear-recent'; id: string; family: typeof RECENT }
  | {
      kind: 'place';
      id: string;
      family: typeof PLACES | typeof COMMANDS;
      path: string;
      place: QuickFindPlace;
      via: 'label' | 'alt';
    }
  | { kind: 'guide'; id: string; family: typeof GUIDES; guide: TourDefinition; title: string }
  | {
      kind: 'coach';
      id: string;
      family: typeof GUIDES;
      entry: CoachEntry;
      title: string;
      step: { at: number; of: number } | null;
    }
  | { kind: 'help-panel'; id: string; family: typeof GUIDES }
  | {
      kind: 'record';
      id: string;
      family: string;
      path: string;
      item: QuickFindItem;
      /** Promoted to "Best match" (QF-02). */
      best: boolean;
    }
  | { kind: 'see-all'; id: string; family: string; path: string; empty: boolean };

/** A group of the listbox, in the order the keyboard walks it. */
interface GroupView {
  family: string;
  choices: Choice[];
  /** A record family: what its "See all" names, and what is on screen of it. */
  listName?: string;
  shown?: number;
  more?: boolean;
}

const NO_CHOICES: Choice[] = [];
const NO_PLACES: QuickFindPlace[] = [];
const NO_GROUPS: GroupView[] = [];

function QuickFindDialog({
  open,
  onClose,
  portal,
  identity,
  placeholder,
  families,
  destinations,
  recent,
}: {
  open: boolean;
  onClose: () => void;
  portal: SearchPortal;
  identity: string;
  placeholder: string;
  families: readonly string[];
  destinations: readonly QuickFindPlace[];
  recent: RecentSearches;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const titleId = useId();
  /*
   * G-2 — what to do once the overlay HAS closed: start the guide or coach
   * chosen in it, or open the Help panel. The guide engine steps aside while
   * any modal dialog is open, so it must not start while this one still is;
   * the dialog's own `close` event is the moment it no longer is.
   */
  const afterClose = useRef<(() => void) | null>(null);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) return;

    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.showModal();
      input.current?.focus();
    }

    if (!open && dialog.open) dialog.close();
  }, [open]);

  const restoreFocus = () => {
    const element = opener.current;
    opener.current = null;

    // An opener that is gone from view (a closed sheet, a removed control):
    // the visible trigger takes the focus instead.
    const usable =
      element instanceof HTMLElement &&
      element.isConnected &&
      element.closest('dialog:not([open])') === null;
    const target = usable
      ? element
      : [...document.querySelectorAll<HTMLElement>('[data-quickfind-trigger]')].find(
          (trigger) => trigger.getClientRects().length > 0,
        );

    target?.focus();
  };

  return (
    <dialog
      ref={ref}
      id="quick-find"
      aria-labelledby={titleId}
      data-quickfind-dialog=""
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={() => {
        restoreFocus();

        const next = afterClose.current;
        afterClose.current = null;
        next?.();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;

        // A click retargeted to the dialog after a row changes beneath the
        // pointer is still inside the panel. Only the actual backdrop closes
        // it; replaying a recent term must leave the new results open.
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          onClose();
      }}
      className={cn(
        'm-0 p-0 text-ink backdrop:bg-[var(--overlay-scrim)] open:flex open:flex-col',
        'bg-card shadow-[var(--shadow-lg)]',
        // Phone: the whole screen, the field at the top — the on-screen
        // keyboard covers the end of the list, never the field or the first rows.
        'max-sm:inset-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none',
        // Desk: a panel anchored near the top, its list scrolling inside it.
        'sm:mx-auto sm:mt-[10vh] sm:mb-auto sm:max-h-[min(36rem,80vh)] sm:w-[min(40rem,calc(100vw-2rem))] sm:rounded-xl',
      )}
    >
      <h2 id={titleId} className="sr-only">
        {t('search.title')}
      </h2>
      {open ? (
        <QuickFindBody
          portal={portal}
          identity={identity}
          placeholder={placeholder}
          families={families}
          inputRef={input}
          onClose={onClose}
          destinations={destinations}
          recent={recent}
          onClosed={(next) => {
            afterClose.current = next;
          }}
        />
      ) : null}
    </dialog>
  );
}

function QuickFindBody({
  portal,
  identity,
  placeholder,
  families,
  inputRef,
  onClose,
  destinations,
  recent,
  onClosed,
}: {
  portal: SearchPortal;
  identity: string;
  placeholder: string;
  families: readonly string[];
  inputRef: RefObject<HTMLInputElement | null>;
  onClose: () => void;
  destinations: readonly QuickFindPlace[];
  recent: RecentSearches;
  /** Runs `next` once the overlay has closed (a guide's or coach's start, the Help panel). */
  onClosed: (next: () => void) => void;
}) {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const guidance = useGuidanceInternals();
  const [term, setTerm] = useState('');
  // What the field holds right now — before the pause that asks the server —
  // so the suggestions of an empty field go as soon as anything is typed.
  const [typed, setTyped] = useState('');
  // Which row is active, for the list it was chosen in: a new answer starts
  // again at its first row without an effect.
  const [cursor, setCursor] = useState({ list: NO_CHOICES, index: 0 });
  const [notice, setNotice] = useState('');
  const leaving = useRef(false);
  const listboxId = useId();
  const optionBase = useId();
  const view = useQuickFind({ portal, identity, term, enabled: true });
  const other: Locale = locale === 'ar' ? 'en' : 'ar';
  const tOther = useMemo(() => i18n.getFixedT(other), [i18n, other]);
  const blank = view.term === '' && normaliseTerm(typed) === '';
  const guided = guidance !== null && guidance.portal === portal;

  // A session that ended (signed out elsewhere, expired, sent back to MFA)
  // takes the overlay and its results down at once.
  useEffect(() => {
    if (endsTheSession(view.error)) onClose();
  }, [view.error, onClose]);

  // G-2 — the guides this seat is offered here: the Help panel's own list;
  // QF-12 — and its guided workflows, with where each one stands.
  const guides = useMemo(
    () =>
      guidance !== null && guidance.portal === portal
        ? availableTours(guidance.catalogue, guidance.ctx)
        : [],
    [guidance, portal],
  );
  const coaches = useMemo(
    () => (guidance !== null && guidance.portal === portal ? coachEntries(guidance) : []),
    [guidance, portal],
  );

  // Recent terms, places, Start commands and Help: matched here, no request.
  const local = useMemo<GroupView[]>(() => {
    const id = (family: string, index: number | string) => `${optionBase}-${family}-${index}`;
    const placeChoice =
      (family: typeof PLACES | typeof COMMANDS) =>
      ({ place, via }: { place: QuickFindPlace; via: 'label' | 'alt' }, index: number): Choice => ({
        kind: 'place',
        id: id(family, index),
        family,
        path: place.to,
        place,
        via,
      });
    const coachChoice = (entry: CoachEntry, index: number): Choice => ({
      kind: 'coach',
      id: id(GUIDES, `coach-${index}`),
      family: GUIDES,
      entry,
      title: t(entry.def.titleKey),
      step: coachStep(guidance, entry),
    });
    const places = destinations.filter((place) => place.kind !== 'command');
    const commands = destinations.filter((place) => place.kind === 'command');
    const groups: GroupView[] = [];
    const add = (family: string, choices: Choice[]) => {
      if (choices.length > 0) groups.push({ family, choices });
    };

    if (blank) {
      // QF-08 — before anything is typed: this person's recent terms, the
      // first places of their navigation (not the page they are on), the
      // Start commands they have, and Help.
      add(RECENT, [
        ...recent.terms.map((value, index): Choice => ({
          kind: 'recent',
          id: id(RECENT, index),
          family: RECENT,
          term: value,
        })),
        ...(recent.terms.length > 0
          ? [{ kind: 'clear-recent', id: id(RECENT, 'clear'), family: RECENT } as const]
          : []),
      ]);
      add(
        PLACES,
        places
          .filter((place) => place.to !== pathname)
          .slice(0, LOCAL_LIMIT)
          .map((place, index) => placeChoice(PLACES)({ place, via: 'label' }, index)),
      );
      add(
        COMMANDS,
        commands
          .slice(0, LOCAL_LIMIT)
          .map((place, index) => placeChoice(COMMANDS)({ place, via: 'label' }, index)),
      );
      add(GUIDES, [
        ...coaches.map(coachChoice),
        ...(guided
          ? [{ kind: 'help-panel', id: id(GUIDES, 'panel'), family: GUIDES } as const]
          : []),
      ]);

      return groups;
    }

    const either = (key: string) =>
      matchesLabel(t(key), view.term) || matchesLabel(tOther(key), view.term);
    // "help" or "guide", in either language, lists every coach and guide;
    // otherwise each is found by its title.
    const asked = view.term.length >= 3 && either('search.help.keywords');

    add(PLACES, matchPlaces(places, view.term).map(placeChoice(PLACES)));
    add(COMMANDS, matchPlaces(commands, view.term).map(placeChoice(COMMANDS)));
    // The coaches first (a handful at most), then up to LOCAL_LIMIT guides:
    // a coach never takes a guide's place in the list.
    add(GUIDES, [
      ...coaches.filter((entry) => asked || either(entry.def.titleKey)).map(coachChoice),
      ...guides
        .filter((guide) => asked || either(guide.titleKey))
        .slice(0, LOCAL_LIMIT)
        .map((guide, index): Choice => ({
          kind: 'guide',
          id: id(GUIDES, index),
          family: GUIDES,
          guide,
          title: t(guide.titleKey),
        })),
    ]);

    return groups;
  }, [
    blank,
    destinations,
    pathname,
    recent.terms,
    coaches,
    guides,
    guided,
    guidance,
    view.term,
    optionBase,
    t,
    tOther,
  ]);

  // Records, the "Best match" first (QF-02), and each list's "See all".
  const answered = useMemo(() => {
    if (view.state !== 'results' && view.state !== 'partial' && view.state !== 'empty')
      return { best: NO_CHOICES, groups: NO_GROUPS };

    const best: Choice[] = [];
    const groups = view.groups.flatMap((group): GroupView[] => {
      const rows = group.items.flatMap((item, index): Choice[] => {
        const path = destinationPath(portal, item.destination);

        return path === null
          ? []
          : [
              {
                kind: 'record',
                id: `${optionBase}-${group.type}-${index}`,
                path,
                family: group.type,
                item,
                best: false,
              },
            ];
      });
      const kept: Choice[] = [];

      for (const row of rows) {
        if (row.kind === 'record' && isExactCode(row.item.code, view.term))
          best.push({ ...row, best: true });
        else kept.push(row);
      }

      // "See all in <list>": the list's own search, when the server offers
      // it — also when nothing was found here, for a list that searches more.
      const all =
        rows.length > 0 || WIDER_LISTS.has(group.type) ? seeAllPath(portal, group.see_all) : null;
      const choices: Choice[] =
        all === null
          ? kept
          : [
              ...kept,
              {
                kind: 'see-all',
                id: `${optionBase}-${group.type}-all`,
                path: all,
                family: group.type,
                empty: rows.length === 0,
              },
            ];

      return choices.length === 0
        ? []
        : [
            {
              family: group.type,
              choices,
              listName: t(`search.scope.${group.type}`),
              shown: kept.length,
              more: group.has_more,
            },
          ];
    });

    return { best, groups };
  }, [view.state, view.groups, view.term, portal, optionBase, t]);

  const groups = useMemo<GroupView[]>(
    () => [
      ...(answered.best.length > 0 ? [{ family: BEST, choices: answered.best }] : []),
      ...local,
      ...answered.groups,
    ],
    [answered, local],
  );
  const choices = useMemo(() => groups.flatMap((group) => group.choices), [groups]);

  // A new answer starts at its first row. The empty field's suggestions start
  // with none active: Enter on an empty field opens nothing by accident.
  const active = cursor.list === choices ? cursor.index : blank ? -1 : 0;
  const setActive = (index: number) => setCursor({ list: choices, index });
  const activeChoice = active < 0 ? null : (choices[Math.min(active, choices.length - 1)] ?? null);

  useEffect(() => {
    if (activeChoice === null) return;

    document.getElementById(activeChoice.id)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeChoice]);

  const search = useCallback((value: string) => {
    setTerm(value);
    setTyped(value);
  }, []);

  const choose = (choice: Choice) => {
    // A recent term is searched again, here: nothing is opened yet.
    if (choice.kind === 'recent') {
      search(choice.term);
      inputRef.current?.focus();

      return;
    }

    if (choice.kind === 'clear-recent') {
      recent.clear();
      setNotice(t('search.recent.cleared'));
      inputRef.current?.focus();

      return;
    }

    // One navigation per choice, however many Enters or presses arrive.
    if (leaving.current) return;

    leaving.current = true;

    if (choice.kind === 'guide' || choice.kind === 'coach' || choice.kind === 'help-panel') {
      const later =
        choice.kind === 'guide'
          ? startGuide(guidance, choice.guide)
          : choice.kind === 'coach'
            ? takeCoach(guidance, choice.entry)
            : openHelp(guidance);

      // Quick Find closes first; the guide, coach or panel follows once it has.
      if (later !== null) onClosed(later);
      onClose();

      return;
    }

    // QF-07: a typed term is kept once a result was opened from it.
    if (choice.kind === 'record' || choice.kind === 'see-all') recent.remember(view.term);

    onClose();
    void navigate(choice.path);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;

    if (event.key === 'Escape') {
      // One behaviour in every browser: Escape closes (the clear control
      // clears). The search field's own "Escape clears" never runs first.
      event.preventDefault();
      onClose();

      return;
    }

    if (choices.length === 0) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // From no active row, Down is the first and Up the last.
      const from = active < 0 ? (step === 1 ? -1 : 0) : active;
      setActive((from + step + choices.length) % choices.length);

      return;
    }

    // QF-19 — the first and the last row of the list.
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActive(event.key === 'Home' ? 0 : choices.length - 1);

      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.repeat || activeChoice === null) return;

      choose(activeChoice);
    }
  };

  const showsList = choices.length > 0;
  const recordsShown = view.state === 'results' || view.state === 'partial';
  const shown = choices.filter(
    (choice) =>
      choice.kind !== 'see-all' && choice.kind !== 'clear-recent' && choice.kind !== 'help-panel',
  ).length;
  const moreIn = answered.groups.filter((group) => group.more).map((group) => group.listName ?? '');
  const listOf = (names: string[]) =>
    new Intl.ListFormat(locale, { type: 'conjunction' }).format(names);
  const announce =
    notice !== ''
      ? notice
      : !blank && (recordsShown || local.length > 0)
        ? [
            t('search.announce', { count: shown }),
            moreIn.length > 0 ? t('search.announceMore', { groups: listOf(moreIn) }) : '',
          ]
            .filter(Boolean)
            .join(' ')
        : '';

  return (
    <>
      <div className="flex items-center gap-2 border-b border-line-subtle p-3">
        <SearchInput
          ref={inputRef}
          value={term}
          onChange={search}
          onInput={(event) => {
            setTyped(event.currentTarget.value);
            setNotice('');
          }}
          debounceMs={DEBOUNCE_MS}
          placeholder={placeholder}
          aria-label={placeholder}
          className="sm:max-w-none"
          maxLength={MAX_TERM}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showsList}
          aria-controls={showsList ? listboxId : undefined}
          aria-activedescendant={showsList && activeChoice ? activeChoice.id : undefined}
          onKeyDown={onKeyDown}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label={t('search.close')}
          className="min-w-11 shrink-0 justify-center"
        >
          <Icon name="close" size={16} />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2" data-quickfind-state={view.state}>
        <StateBody
          view={view}
          blank={blank}
          scope={scopeOf(families, locale, t)}
          locale={locale}
          listboxId={listboxId}
          groups={groups}
          localCount={local.length}
          activeId={activeChoice?.id ?? null}
          onHover={(id) => setActive(choices.findIndex((choice) => choice.id === id))}
          onChoose={choose}
        />
      </div>

      {/* Announced, not shown: how many rows are on screen (no server count). */}
      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>

      <p className="type-meta flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line-subtle px-4 py-2 text-ink-muted max-sm:hidden">
        <span className="sr-only">{t('search.keys.label')}</span>
        <span>
          <kbd className="numeric">↑↓</kbd> {t('search.keys.move')}
        </span>
        <span>
          <kbd>Enter</kbd> {t('search.keys.open')}
        </span>
        <span>
          <kbd>Esc</kbd> {t('search.keys.close')}
        </span>
      </p>
    </>
  );
}

/**
 * G-2 — how a guide chosen in Quick Find starts, as the Help panel starts it:
 * one in progress resumes where it stopped, any other starts at its first
 * visible step. Returned as the thing to run once the overlay has closed; the
 * focus is back on the entry by then, and returns there when the guide ends.
 */
function startGuide(guidance: GuidanceInternals | null, def: TourDefinition): (() => void) | null {
  if (guidance === null) return null;

  const { ctx, progress, store } = guidance;
  const steps = visibleSteps(def, ctx);
  const stored = progress.status(def) === 'in_progress' ? progress.resumeStep(def) : undefined;
  const from = steps.find((step) => step.id === stored) ?? steps.at(0);

  if (from === undefined) return null;

  return () => store.start({ tourId: def.id, mode: 'full', stepId: from.id, opener: focused() });
}

/** Where focus is once the overlay has closed: the entry it returned to. */
function focused(): HTMLElement | null {
  const active = document.activeElement;

  return active instanceof HTMLElement && active !== document.body ? active : null;
}

/** QF-12 — where an unfinished coach stands: "step n of m" (it continues there). */
function coachStep(
  guidance: GuidanceInternals | null,
  entry: CoachEntry,
): { at: number; of: number } | null {
  if (guidance === null || entry.status !== 'in_progress') return null;

  const steps = visibleCoachSteps(entry.def, guidance.ctx);

  if (steps.length === 0) return null;

  return {
    at: resumeIndex(steps, guidance.progress.coachResumeStep(entry.def)) + 1,
    of: steps.length,
  };
}

/**
 * QF-12 — a coach chosen in Quick Find, through the coaches' own entry
 * points (as Help & tours does): the one running is shown again, one in
 * progress continues where it stopped, any other starts at its first step.
 * The coach only ever follows the person; nothing is done for them.
 */
function takeCoach(guidance: GuidanceInternals | null, entry: CoachEntry): (() => void) | null {
  if (guidance === null) return null;

  return () => {
    if (entry.running) {
      guidance.store.setMinimised(false);

      return;
    }

    if (entry.status === 'in_progress')
      continueCoach(guidance, entry.def.id, { opener: focused() });
    else startCoach(guidance, entry.def.id, { opener: focused() });
  };
}

/** "All help and tours": the shell's Help panel, focus back on the entry after it. */
function openHelp(guidance: GuidanceInternals | null): (() => void) | null {
  if (guidance === null) return null;

  return () => guidance.store.openPanel({ returnFocusTo: focused() });
}

function isTooMany(error: unknown): boolean {
  return error instanceof ApiError && error.status === 429;
}

function StateBody({
  view,
  blank,
  scope,
  locale,
  listboxId,
  groups,
  localCount,
  activeId,
  onHover,
  onChoose,
}: {
  view: QuickFindView;
  blank: boolean;
  scope: string;
  locale: Locale;
  listboxId: string;
  groups: GroupView[];
  localCount: number;
  activeId: string | null;
  onHover: (id: string) => void;
  onChoose: (choice: Choice) => void;
}) {
  const { t } = useTranslation();
  const listOf = (names: string[]) =>
    new Intl.ListFormat(locale, { type: 'conjunction' }).format(names);
  const answered = view.state === 'results' || view.state === 'partial' || view.state === 'empty';
  // The families searched with nothing found, named once below the matches —
  // "no number or name" for a list that searches more fields (QF-11).
  const searchedEmpty = answered ? view.groups.filter((group) => group.items.length === 0) : [];
  const emptyNarrow = searchedEmpty.filter((group) => !WIDER_LISTS.has(group.type));
  const emptyWide = searchedEmpty.filter((group) => WIDER_LISTS.has(group.type));
  const busy = view.state === 'loading';

  const headingOf = (group: GroupView): string =>
    group.family === BEST ? t('search.group.best') : t(`search.group.${group.family}`);

  const list =
    groups.length > 0 ? (
      <div
        role="listbox"
        id={listboxId}
        aria-label={t('search.results')}
        aria-busy={busy || undefined}
      >
        {groups.map((group) => {
          const headingId = `${listboxId}-${group.family}`;
          const countId = `${headingId}-count`;
          const counted = group.shown !== undefined && group.shown > 0;

          return (
            <div
              key={group.family}
              role="group"
              aria-labelledby={headingId}
              aria-describedby={counted ? countId : undefined}
              data-quickfind-group={group.family}
              className="py-1"
            >
              <div
                role="presentation"
                className="flex flex-wrap items-baseline gap-x-2 px-3 pb-1 pt-2"
              >
                <span id={headingId} className="type-table-header text-ink-muted">
                  {headingOf(group)}
                </span>
                {counted ? (
                  <span id={countId} className="type-meta text-ink-muted">
                    {t('search.shown', { count: group.shown })}
                    {group.more ? ` · ${t('search.moreAvailable')}` : ''}
                  </span>
                ) : null}
              </div>
              {group.choices.map((choice) => (
                <Row
                  key={choice.id}
                  choice={choice}
                  term={view.term}
                  locale={locale}
                  active={choice.id === activeId}
                  listName={group.listName ?? ''}
                  onHover={onHover}
                  onChoose={onChoose}
                />
              ))}
            </div>
          );
        })}
      </div>
    ) : null;

  if (view.state === 'initial') {
    return (
      <>
        {blank ? (
          <p className="type-meta px-3 pb-1 pt-2 text-ink-muted">
            {t('search.initialScope', { scope })}
          </p>
        ) : null}
        {list}
        {groups.length === 0 ? (
          <p className="type-ui px-2 py-6 text-center text-ink-muted">{t('search.initial')}</p>
        ) : null}
      </>
    );
  }

  // Nothing at all to open: the record search said so, and nothing local
  // matched. A list that searches more fields is still offered (QF-11).
  if (view.state === 'empty' && localCount === 0) {
    return (
      <>
        <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
          <p className="type-card-title text-ink-heading">
            {t('search.noResults', { term: view.term })}
          </p>
          <p className="type-meta text-ink-muted">{t('search.noResultsHint')}</p>
        </div>
        {list}
      </>
    );
  }

  return (
    <>
      {view.state === 'partial' ? (
        <div
          role="status"
          className="m-2 flex flex-wrap items-center gap-2 rounded-lg border border-warning-line bg-warning-surface p-3"
        >
          <p className="type-ui min-w-0 flex-1 text-warning-ink">
            {t('search.partial', {
              groups: listOf(view.unavailable.map((family) => t(`search.scope.${family}`))),
            })}
            <span className="type-meta block">{t('search.partialHint')}</span>
          </p>
          <Button variant="secondary" size="sm" onClick={view.retry}>
            {t('app.retry')}
          </Button>
        </div>
      ) : null}

      {list}

      {busy ? (
        <>
          {view.previous !== null ? (
            <PreviousAnswer previous={view.previous} locale={locale} />
          ) : null}
          <LoadingState rows={view.previous !== null ? 0 : 3} label={t('search.searching')} />
        </>
      ) : null}

      {view.state === 'error' ? (
        <div
          role="alert"
          className="m-2 space-y-2 rounded-lg border border-critical-line bg-critical-surface p-4"
        >
          <p className="type-ui font-medium text-critical-ink">
            {t(isTooMany(view.error) ? 'search.tooMany' : 'search.failed')}
          </p>
          <p className="type-meta text-critical-ink">
            {t(isTooMany(view.error) ? 'search.tooManyHint' : 'search.failedHint')}
          </p>
          <Button variant="secondary" size="sm" onClick={view.retry}>
            {t('app.retry')}
          </Button>
        </div>
      ) : null}

      {emptyNarrow.length > 0 ? (
        <p className="type-meta px-3 py-2 text-ink-muted">
          {t('search.noneIn', {
            groups: listOf(emptyNarrow.map((empty) => t(`search.scope.${empty.type}`))),
          })}
        </p>
      ) : null}
      {emptyWide.length > 0 ? (
        <p className="type-meta px-3 py-2 text-ink-muted">
          {t('search.noneInByName', {
            groups: listOf(emptyWide.map((empty) => t(`search.scope.${empty.type}`))),
          })}
        </p>
      ) : null}
    </>
  );
}

/**
 * QF-19 — the previous term's answer while the next one is asked: dimmed,
 * marked busy, and out of reach (no option, no pointer) — it is not this
 * term's answer, so Enter can never open one of its rows by mistake.
 */
function PreviousAnswer({
  previous,
  locale,
}: {
  previous: { term: string; groups: QuickFindGroup[] };
  locale: Locale;
}) {
  const { t } = useTranslation();

  return (
    <div
      aria-hidden="true"
      data-quickfind-previous=""
      className="pointer-events-none select-none opacity-50"
    >
      {previous.groups
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <div key={group.type} className="py-1">
            <div className="type-table-header px-3 pb-1 pt-2 text-ink-muted">
              {t(`search.group.${group.type}`)}
            </div>
            {group.items.map((item) => (
              <div key={item.id} className="flex min-h-11 items-center gap-3 px-3 py-2">
                <RecordText item={item} term={previous.term} locale={locale} />
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}

function Row({
  choice,
  term,
  locale,
  active,
  listName,
  onHover,
  onChoose,
}: {
  choice: Choice;
  term: string;
  locale: Locale;
  active: boolean;
  listName: string;
  onHover: (id: string) => void;
  onChoose: (choice: Choice) => void;
}) {
  const { t, i18n } = useTranslation();

  const press = {
    id: choice.id,
    role: 'option' as const,
    'aria-selected': active,
    'data-quickfind-kind': choice.kind,
    // Keep the focus in the field: the list is driven from there.
    onMouseDown: (event: ReactMouseEvent) => event.preventDefault(),
    onMouseMove: () => onHover(choice.id),
    onClick: () => onChoose(choice),
  };
  const rowClass = cn(
    'flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-start',
    active ? 'bg-selected' : 'hover:bg-hover',
  );

  if (choice.kind === 'see-all') {
    return (
      <div {...press} data-quickfind-see-all="" className={cn(rowClass, 'text-link')}>
        <span className="type-ui min-w-0 flex-1">
          {choice.empty
            ? t('search.seeAllFor', { list: listName, term })
            : t('search.seeAll', { list: listName })}
        </span>
        <Icon name="chevron" size={16} className="shrink-0 rtl:-scale-x-100" />
      </div>
    );
  }

  // QF-07 — a recent term: chosen, it is searched again (nothing opens yet).
  if (choice.kind === 'recent') {
    return (
      <div {...press} data-quickfind-label={choice.term} className={rowClass}>
        <Icon name="search" size={16} className="shrink-0 text-ink-muted" />
        <span dir="auto" className="type-ui min-w-0 flex-1 truncate text-ink">
          {choice.term}
        </span>
      </div>
    );
  }

  if (choice.kind === 'clear-recent') {
    return (
      <div {...press} className={cn(rowClass, 'text-link')}>
        <span className="type-ui min-w-0 flex-1">{t('search.recent.clear')}</span>
      </div>
    );
  }

  // G-1 — a place the navigation offers, or a Start command: its label, and
  // where it sits there. Matched on the other language's label, that label
  // is shown beside it in its own language, so the match is visible (QF-09).
  if (choice.kind === 'place') {
    const { place } = choice;
    const other: Locale = locale === 'ar' ? 'en' : 'ar';
    const meta =
      choice.family === COMMANDS ? t('search.commandHint') : (place.context ?? undefined);

    return (
      <div {...press} data-quickfind-label={place.label} className={rowClass}>
        <div className="min-w-0 flex-1">
          <div className="type-ui truncate text-ink">
            <Highlight
              text={place.label}
              ranges={choice.via === 'label' ? labelRanges(place.label, term) : NO_RANGES}
            />
          </div>
          {meta || choice.via === 'alt' ? (
            <div className="type-meta truncate text-ink-muted">
              {meta}
              {meta && choice.via === 'alt' ? ' · ' : null}
              {choice.via === 'alt' && place.altLabel ? (
                <bdi lang={other}>
                  <Highlight text={place.altLabel} ranges={labelRanges(place.altLabel, term)} />
                </bdi>
              ) : null}
            </div>
          ) : null}
        </div>
        <Icon name="chevron" size={16} className="shrink-0 text-ink-muted rtl:-scale-x-100" />
      </div>
    );
  }

  // G-2 — a guide: its title and what it covers. It shows; it never acts.
  if (choice.kind === 'guide') {
    return (
      <div {...press} data-quickfind-label={choice.title} className={rowClass}>
        <Icon name="help" size={18} className="shrink-0 text-ink-muted" />
        <div className="min-w-0 flex-1">
          <div className="type-ui truncate text-ink">
            <Highlight text={choice.title} ranges={labelRanges(choice.title, term)} />
          </div>
          <div className="type-meta truncate text-ink-muted">{t(choice.guide.summaryKey)}</div>
        </div>
      </div>
    );
  }

  // QF-12 — a guided workflow, and where it stands for this person.
  if (choice.kind === 'coach') {
    const { entry, step } = choice;
    const state = entry.running
      ? t('search.coach.running')
      : step !== null
        ? t('search.coach.continue', { step: step.at, total: step.of })
        : entry.status === 'completed'
          ? t('search.coach.completed')
          : t('search.coach.start');

    return (
      <div {...press} data-quickfind-label={choice.title} className={rowClass}>
        <Icon name="help" size={18} className="shrink-0 text-ink-muted" />
        <div className="min-w-0 flex-1">
          <div className="type-ui truncate text-ink">
            <Highlight text={choice.title} ranges={labelRanges(choice.title, term)} />
          </div>
          <div className="type-meta truncate text-ink-muted">
            {t('search.coach.kind')} · {state}
          </div>
        </div>
        <Icon name="chevron" size={16} className="shrink-0 text-ink-muted rtl:-scale-x-100" />
      </div>
    );
  }

  if (choice.kind === 'help-panel') {
    return (
      <div {...press} className={rowClass}>
        <Icon name="help" size={18} className="shrink-0 text-ink-muted" />
        <div className="min-w-0 flex-1">
          <div className="type-ui truncate text-ink">{t('search.help.all')}</div>
          <div className="type-meta truncate text-ink-muted">{t('search.help.allHint')}</div>
        </div>
      </div>
    );
  }

  const item = choice.item;
  const statusKey = item.status ? `search.status.${choice.family}.${item.status}` : null;
  const statusLabel = statusKey && i18n.exists(statusKey) ? t(statusKey) : null;

  return (
    <div {...press} data-quickfind-best={choice.best ? '' : undefined} className={rowClass}>
      <div className="min-w-0 flex-1">
        <RecordText
          item={item}
          term={term}
          locale={locale}
          kind={choice.best ? t(`search.group.${choice.family}`) : undefined}
        />
      </div>
      {statusLabel && item.status ? <StatusBadge status={item.status} label={statusLabel} /> : null}
    </div>
  );
}

const NO_RANGES: readonly TextRange[] = [];

/**
 * A record's code, title and context, the match marked (QF-01). A title or
 * context shown in the other language — the record has none in this one —
 * carries its own `lang` in an isolating `<bdi>` (QF-10, DS-04). Codes stay
 * in the LTR-isolated numeric face.
 */
function RecordText({
  item,
  term,
  locale,
  kind,
}: {
  item: QuickFindItem;
  term: string;
  locale: Locale;
  /** In "Best match": what kind of record it is (its family's name). */
  kind?: string;
}) {
  const other: Locale = locale === 'ar' ? 'en' : 'ar';
  const title = item.title?.[locale] || item.title?.[other] || '';
  const context = item.context ? item.context[locale] || item.context[other] : '';
  const code = item.code && item.code !== title ? item.code : null;

  return (
    <>
      <div className="flex min-w-0 items-baseline gap-2">
        {code ? (
          <span className="numeric type-ui shrink-0 font-medium text-ink-heading">
            <Highlight text={code} ranges={substringRanges(code, term)} />
          </span>
        ) : null}
        <bdi
          dir="auto"
          lang={foreignLang(title, locale)}
          className="type-ui min-w-0 truncate text-ink"
        >
          <Highlight text={title} ranges={substringRanges(title, term)} />
        </bdi>
      </div>
      {context || kind ? (
        <div className="type-meta truncate text-ink-muted">
          {kind}
          {kind && context ? ' · ' : null}
          {context ? (
            <bdi dir="auto" lang={foreignLang(context, locale)}>
              {context}
            </bdi>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
